import { useEffect, useState } from 'react';
import { Link, Navigate, NavLink, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, ArrowUpDown, LineChart, ClipboardCheck, FileText,
  MessagesSquare, Plug, ShieldCheck, KeyRound, Users2, Settings as SettingsIcon,
  Upload as UploadIcon, Menu, X, LogOut, ChevronsLeft, ChevronsRight,
  Table2, Building2, ArrowLeftRight, UserSquare2, UserCircle2, Languages,
  Sparkles, SlidersHorizontal, Search, BarChart3, Split, Layers, Grid3x3, Copy, GitBranch, Gauge, Radar,
} from 'lucide-react';
import { LANGUAGES } from '@/i18n/languages';
import { markManualLanguage } from '@/i18n/manualLanguage';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { displayFy, displayFyYear } from '@/lib/fiscalYear';
import { useCurrentUser } from '@/hooks/useAuth';
import { authApi } from '@/lib/auth-api';
import { api } from '@/lib/api';
import { AiCostMeter } from '@/components/AiCostMeter';
import { NotificationBell } from '@/components/NotificationBell';
import { CommandPalette } from '@/components/app/CommandPalette';
import { cn } from '@/lib/cn';
import { UnverifiedEmailBanner } from '@/components/app/UnverifiedEmailBanner';
import { TrialBanner } from '@/components/app/TrialBanner';

interface Workspace {
  id: string;
  name: string;
  fyLabel: string;
  companyId: string;
}

/**
 * Editorial app shell. All authenticated app pages render inside this.
 *
 * Layout:
 *   - LEFT: collapsible sidebar (240px wide / 64px collapsed). Slate body,
 *     emerald-accented active nav, hairline borders. Choice persists to
 *     localStorage so the user's preference survives page nav.
 *   - TOP: thin masthead with workspace name + FY chip, AI cost meter,
 *     notifications, and a user popover whose only meaningful action is
 *     "Sign out" — clicked once, logs out and routes to /login (no
 *     confirmation modal, per house style).
 *   - MAIN: passes children straight through. Each page is responsible
 *     for its own padding so dense tables can go edge-to-edge if needed.
 *
 * The shell does the auth gate (loading → /login redirect) so individual
 * pages don't repeat it.
 */
export function AppShell({
  children,
  title,
  subtitle,
  pageActions,
}: {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  pageActions?: React.ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const { data: user, isLoading: userLoading } = useCurrentUser();
  const nav = useNavigate();
  const qc = useQueryClient();
  const loc = useLocation();

  // Sidebar collapsed state — persisted across navigations.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('cadencez.shell.collapsed') === '1';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('cadencez.shell.collapsed', collapsed ? '1' : '0');
    }
  }, [collapsed]);

  // Mobile drawer state.
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Remember the last workspace the user was in. Workspace-less routes
  // (/app/profile, /app/admin) have no :wsId, which would otherwise grey out
  // the entire sidebar. Falling back to the remembered id keeps the nav live
  // so the user can jump straight back into their workspace.
  const [storedWsId, setStoredWsId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem('cadencez.shell.lastWorkspace');
  });
  useEffect(() => {
    if (wsId && typeof window !== 'undefined') {
      window.localStorage.setItem('cadencez.shell.lastWorkspace', wsId);
      setStoredWsId(wsId);
    }
  }, [wsId]);
  const effectiveWsId = wsId ?? storedWsId ?? undefined;

  const { data: workspace } = useQuery({
    queryKey: ['workspace', effectiveWsId],
    queryFn: async () => (await api.get<Workspace>(`/api/workspaces/${effectiveWsId}`)).data,
    enabled: !!effectiveWsId && !!user,
  });

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">
        {t('common.loading')}
      </div>
    );
  }
  if (!user) {
    return <Navigate to={`/login?auth=required&next=${encodeURIComponent(loc.pathname)}`} replace />;
  }

  const isAdmin = (user as { isAdmin?: boolean } | undefined)?.isAdmin === true;

  // The single, deduplicated nav set. Items disable themselves when there's no
  // workspace context at all — neither on the URL (:wsId) nor remembered from a
  // prior visit (effectiveWsId). On /app/profile etc. they stay live and point
  // back at the last workspace.
  const navItems = [
    { to: `/app/workspace/${effectiveWsId}`,                  icon: LayoutDashboard, label: t('nav.dashboard'),  end: true,  needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/upload`,           icon: UploadIcon,      label: t('workspace.upload_data'), needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/import-mappings`,  icon: Copy,            label: t('nav.import_mappings', { defaultValue: 'Import mappings' }), needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/workbooks`,        icon: Table2,          label: t('nav.workbooks'),   needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/entities`,         icon: Building2,       label: t('nav.entities'),    needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/org-changes`,      icon: GitBranch,       label: t('nav.org_changes', { defaultValue: 'Org changes' }), needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/fx-rates`,         icon: ArrowLeftRight,  label: t('nav.fx_rates'),    needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/workforce`,        icon: UserSquare2,     label: t('nav.workforce'),   needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/forecast`,         icon: LineChart,       label: t('nav.forecast'),    needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/planning`,         icon: SlidersHorizontal, label: t('nav.planning'),  needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/compilation`,      icon: Layers,          label: t('nav.compilation', { defaultValue: 'Budget compilation' }), needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/allocations`,      icon: Split,           label: t('nav.allocations', { defaultValue: 'Cost allocation' }), needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/pl`,               icon: BarChart3,       label: t('nav.pl_report', { defaultValue: 'P&L Report' }), needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/pivot`,            icon: Grid3x3,         label: t('nav.pivot', { defaultValue: 'Pivot report' }), needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/kpis`,             icon: Gauge,           label: t('nav.kpis', { defaultValue: 'KPIs' }), needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/signals`,          icon: Radar,           label: t('nav.signals', { defaultValue: 'Signals' }), needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/board-pack`,       icon: FileText,        label: t('nav.board_pack'),  needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/approvals`,        icon: ClipboardCheck,  label: t('nav.approvals'),   needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/qa`,               icon: MessagesSquare,  label: t('nav.qa'),          needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/ai-results`,       icon: Sparkles,        label: t('nav.ai_results'),  needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/account-search`,   icon: Search,          label: t('nav.account_search'), needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/connectors`,       icon: Plug,            label: t('nav.connectors'),  needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/audit-log`,        icon: ShieldCheck,     label: t('nav.audit_log'),   needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/sso`,              icon: KeyRound,        label: t('nav.sso'),         needsWs: true, gap: true },
    { to: `/app/workspace/${effectiveWsId}/scim`,             icon: Users2,          label: t('nav.scim'),        needsWs: true },
    { to: `/app/workspace/${effectiveWsId}/settings`,         icon: SettingsIcon,    label: t('nav.settings'),    needsWs: true },
  ];

  async function onLogout() {
    // No confirm dialog — direct sign-out per house style.
    try { await authApi.logout(); } catch { /* token cleanup is best-effort */ }
    qc.clear();
    nav('/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-ink-50/40 flex">
      {/* Mobile-only drawer overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/30 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:sticky top-0 z-50 h-screen shrink-0 flex flex-col bg-background border-r border-border',
          'transition-[width,transform] duration-200',
          'print:hidden',
          collapsed ? 'w-16' : 'w-60',
          // Mobile drawer slides in from the start edge — flip the
          // exit translation under RTL so it leaves to the right instead
          // of the left (where there's no edge to disappear behind).
          mobileOpen ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full lg:translate-x-0',
        )}
      >
        {/* Brand row */}
        <div className={cn('h-16 flex items-center border-b border-border', collapsed ? 'px-3 justify-center' : 'px-5')}>
          <Link to="/app" className="flex items-center gap-2.5">
            <img
              src="/logo.png"
              alt={t('brand.name')}
              className="h-8 w-8 rounded-md object-contain shrink-0"
            />
            {!collapsed && (
              <span className="display text-[17px] font-semibold tracking-tight">{t('brand.name')}</span>
            )}
          </Link>
        </div>

        {/* Workspace chip */}
        {workspace && (
          <div className={cn('border-b border-border', collapsed ? 'px-2 py-2' : 'px-3 py-3')}>
            <div className={cn('rounded-md bg-ink-100/70', collapsed ? 'px-1.5 py-1.5 text-center' : 'px-3 py-2')}>
              {!collapsed ? (
                <>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-medium">
                    {t('common.workspace') ?? 'Workspace'}
                  </p>
                  <p className="mt-0.5 text-[13px] font-semibold truncate">{workspace.name}</p>
                  <p className="num text-[10.5px] text-muted-foreground">{displayFy(workspace.fyLabel, i18n.language)}</p>
                </>
              ) : (
                <span
                  className="inline-block num text-[9px] uppercase tracking-wide text-muted-foreground"
                  title={`${workspace.name} · ${displayFy(workspace.fyLabel, i18n.language)}`}
                >
                  {displayFyYear(workspace.fyLabel, i18n.language)}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Primary nav */}
        <nav className="flex-1 overflow-y-auto py-3 cadencez-scrollbar">
          <ul className="space-y-0.5">
            {navItems.map((it) => {
              const disabled = it.needsWs && !effectiveWsId;
              return (
                <li key={it.to} className={it.gap ? 'mt-3 pt-3 border-t border-border/60' : ''}>
                  {disabled ? (
                    <span
                      className={cn(
                        'flex items-center gap-3 px-3 mx-2 h-9 rounded text-muted-foreground/60 text-[13px] cursor-not-allowed',
                        collapsed && 'justify-center px-0',
                      )}
                    >
                      <it.icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
                      {!collapsed && <span>{it.label}</span>}
                    </span>
                  ) : (
                    <NavLink
                      to={it.to}
                      end={it.end}
                      className={({ isActive }) =>
                        cn(
                          'relative flex items-center gap-3 mx-2 h-9 rounded text-[13px] transition-colors',
                          collapsed ? 'justify-center px-0' : 'px-3',
                          isActive
                            ? 'bg-foreground/[0.06] text-foreground font-medium'
                            : 'text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <span className="absolute start-0 inset-y-1.5 w-0.5 bg-emerald-600 rounded-e" />
                          )}
                          <it.icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
                          {!collapsed && <span className="truncate">{it.label}</span>}
                        </>
                      )}
                    </NavLink>
                  )}
                </li>
              );
            })}
            {isAdmin && (
              <li className="mt-3 pt-3 border-t border-border/60">
                <NavLink
                  to="/app/admin"
                  className={({ isActive }) =>
                    cn(
                      'relative flex items-center gap-3 mx-2 h-9 rounded text-[13px] transition-colors',
                      collapsed ? 'justify-center px-0' : 'px-3',
                      isActive
                        ? 'bg-emerald-600 text-white font-medium'
                        : 'text-emerald-700 hover:bg-emerald-50',
                    )
                  }
                >
                  <ShieldCheck className="h-4 w-4 shrink-0" strokeWidth={2} />
                  {!collapsed && <span>{t('nav.admin') ?? 'Admin'}</span>}
                </NavLink>
              </li>
            )}
          </ul>
        </nav>

        {/* Collapse / user footer */}
        <div className="border-t border-border">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className={cn(
              'hidden lg:flex w-full items-center gap-2 px-4 h-9 text-[11px] uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]',
              collapsed && 'justify-center px-0',
            )}
          >
            {collapsed ? <ChevronsRight className="h-3.5 w-3.5" /> : <><ChevronsLeft className="h-3.5 w-3.5" /> {t('common.collapse') ?? 'Collapse'}</>}
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-30 h-16 bg-background/95 backdrop-blur border-b border-border flex items-center px-4 sm:px-6 gap-3 print:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-md border border-border hover:bg-foreground/[0.04]"
            aria-label={t('common.open_menu', { defaultValue: 'Open menu' })}
          >
            <Menu className="h-4 w-4" />
          </button>

          <div className="flex-1 min-w-0">
            {wsId && title && loc.pathname !== `/app/workspace/${wsId}` && (
              <nav className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-0.5 truncate" aria-label={t('common.breadcrumb', { defaultValue: 'Breadcrumb' })}>
                <Link to={`/app/workspace/${wsId}`} className="hover:text-foreground truncate">
                  {workspace?.name ?? t('common.workspace') ?? 'Workspace'}
                </Link>
                <span className="text-ink-300">/</span>
                <span className="text-foreground/70 truncate">{title}</span>
              </nav>
            )}
            {title && (
              <h1 className="display text-[17px] font-semibold tracking-tight truncate">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className="text-[12px] text-muted-foreground truncate">{subtitle}</p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {pageActions}
            <div className="hidden sm:block">
              <CommandPalette wsId={effectiveWsId} />
            </div>
            {wsId && <AiCostMeter workspaceId={wsId} />}
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>
            <NotificationBell />
            <UserMenu
              user={user as { name: string | null; email: string | null; pictureUrl?: string | null }}
              isAdmin={isAdmin}
              onLogout={onLogout}
            />
          </div>
        </header>

        <UnverifiedEmailBanner />
        <TrialBanner wsId={wsId} companyId={workspace?.companyId} />

        {/* Page content */}
        <main className="flex-1 min-h-0">{children}</main>
      </div>
    </div>
  );
}

/**
 * User popover. Click the chip → dropdown with name/email and a single
 * "Sign out" item. No confirm — clicked = signed out.
 */
function UserMenu({
  user, isAdmin, onLogout,
}: {
  user: { name: string | null; email: string | null; pictureUrl?: string | null };
  isAdmin: boolean;
  onLogout: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Click-outside dismissal.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest('[data-usermenu]');
      if (!el) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const initial = (user.name || user.email || '?').trim().charAt(0).toUpperCase();

  return (
    <div data-usermenu className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 h-9 ps-1 pe-2.5 rounded-md border border-border hover:bg-foreground/[0.04] transition-colors"
        aria-label={t('common.user_menu', { defaultValue: 'User menu' })}
      >
        {user.pictureUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={user.pictureUrl} alt="" className="h-7 w-7 rounded object-cover" />
        ) : (
          <span className="inline-flex h-7 w-7 items-center justify-center rounded bg-foreground text-background text-[11px] font-semibold">
            {initial}
          </span>
        )}
        <span className="hidden sm:inline text-[12.5px] font-medium truncate max-w-[8rem]">
          {user.name || user.email}
        </span>
      </button>

      {open && (
        <div
          className="absolute end-0 top-11 w-72 rounded-md border border-border bg-background shadow-editorial overflow-hidden"
          role="menu"
        >
          <div className="px-3 py-3 border-b border-border">
            <p className="text-[13px] font-semibold truncate">{user.name || '—'}</p>
            <p className="text-[11.5px] text-muted-foreground truncate">{user.email || '—'}</p>
            {isAdmin && (
              <span className="mt-2 inline-block rounded text-[9px] uppercase tracking-[0.16em] font-semibold pill-emerald px-1.5 py-0.5">
                admin
              </span>
            )}
          </div>

          <Link
            to="/app/profile"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-foreground hover:bg-ink-100"
            role="menuitem"
          >
            <UserCircle2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
            {t('account.title')}
          </Link>

          <UserMenuSettings onSelected={() => setOpen(false)} />

          <UserMenuLanguage onSelected={() => setOpen(false)} />

          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-foreground hover:bg-ink-100 border-t border-border"
            role="menuitem"
          >
            <LogOut className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
            {t('common.logout')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Inline language quick-toggle inside the user dropdown. Shows the
 * current language as a row header; click to expand into a scrollable
 * native-name list. Persistence is i18next-only (matches the existing
 * SiteHeader switcher); the ProfilePage variant additionally writes
 * users.primary_locale to the server so it survives device changes.
 */
function UserMenuSettings({ onSelected }: { onSelected: () => void }) {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  if (!wsId) return null;
  return (
    <Link
      to={`/app/workspace/${wsId}/settings`}
      onClick={onSelected}
      className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-foreground hover:bg-ink-100"
      role="menuitem"
    >
      <SettingsIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
      {t('nav.settings')}
    </Link>
  );
}

function UserMenuLanguage({ onSelected }: { onSelected: () => void }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const current = (i18n.language || 'en').split('-')[0];
  const currentLang = LANGUAGES.find((l) => l.code === current);

  return (
    <div className="border-t border-border">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2.5 px-3 py-2.5 text-[13px] text-foreground hover:bg-ink-100"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-2.5 truncate">
          <Languages className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
          <span className="truncate">{t('common.language')}</span>
        </span>
        <span className="text-[11.5px] text-muted-foreground">
          {currentLang ? `${currentLang.flag} ${currentLang.nativeName}` : current}
        </span>
      </button>
      {expanded && (
        <ul className="max-h-56 overflow-y-auto bg-ink-50 border-t border-border">
          {LANGUAGES.map((l) => {
            const active = l.code === current;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => {
                    markManualLanguage();
                    void i18n.changeLanguage(l.code);
                    setExpanded(false);
                    onSelected();
                  }}
                  className={
                    'w-full flex items-center gap-2 px-3 py-2 text-[12.5px] hover:bg-white ' +
                    (active ? 'font-semibold text-ink-900' : 'text-ink-700')
                  }
                >
                  <span>{l.flag}</span>
                  <span className="truncate">{l.nativeName}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Tiny utility — a hairline-bordered editorial section header to use
 * inside page bodies. Mirrors the landing-section eyebrow style.
 */
export function PageSection({
  eyebrow, title, children, actions,
}: {
  eyebrow?: string;
  title?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      {(eyebrow || title || actions) && (
        <div className="flex items-baseline justify-between mb-4 gap-3">
          <div className="min-w-0">
            {eyebrow && (
              <p className="text-[10.5px] uppercase tracking-[0.22em] font-medium text-emerald-700">
                {eyebrow}
              </p>
            )}
            {title && (
              <h2 className="display mt-1 text-xl md:text-2xl font-medium tracking-tight">{title}</h2>
            )}
          </div>
          {actions && <div className="shrink-0 flex items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}
